import { z } from 'zod';
import { roundToCents } from '../money-math';
import type { QuoteCalcResult } from './quote-calculation-engine';
import type { QuoteCalcGroup, QuoteCalcGroupKey, QuoteCalcLine } from './quote-calculation.schema';
import { offerLineText, offerOptionText, type OfferLanguage, type QuoteOffer, type QuoteOfferCalc } from './quote-offer';

/**
 * Übergabe einer gewonnenen Kalkulation an Verkauf und Projekt (ADR 0112):
 * Auftrag mit einer Pauschalzeile (plus angenommene Optionen als eigene Zeilen),
 * Projekt mit Vorkalkulation aus den EK der Positionen. Reine Rechnung, damit
 * Oberfläche (Vorschau) und Server dasselbe ergeben.
 */

export const quoteHandoverSchema = z.object({
  /** Optionspositionen, die der Kunde mitbestellt hat. */
  acceptedOptionLineIds: z.array(z.string().trim().min(1).max(64)).max(300).default([]),
  project: z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('NEW'), name: z.string().trim().min(1).max(200) }),
    z.object({ mode: z.literal('EXISTING'), projectId: z.string().uuid() }),
  ]),
  /** Bestellnummer des Kunden. */
  customerReference: z.string().trim().max(200).nullable().optional(),
  /**
   * Nach dem Entsperren gibt es den Auftrag der ersten Übergabe noch — eine weitere
   * Übergabe legt einen zweiten an und muss das ausdrücklich bestätigen.
   */
  confirmAdditionalOrder: z.boolean().optional(),
});
export type QuoteHandoverInput = z.infer<typeof quoteHandoverSchema>;

export const quoteHandoverResultSchema = z.object({
  salesDocumentId: z.string().uuid(),
  salesDocumentNumber: z.string(),
  projectId: z.string().uuid(),
  projectCode: z.string(),
  calcLineCount: z.number().int(),
  isPdfAttached: z.boolean(),
});
export type QuoteHandoverResult = z.infer<typeof quoteHandoverResultSchema>;

export interface QuoteHandoverOrderLine {
  kind: 'LUMP_SUM' | 'OPTION';
  lineId: string | null;
  description: string;
  longText: string;
  /** Auf Cent gerundet — wie im Angebot gedruckt (gebucht wird, was gedruckt wird). */
  unitPriceMicros: bigint;
}

const OPTION_PREFIX: Record<OfferLanguage, string> = { de: 'Option', en: 'Option' };
const OFFER_REF: Record<OfferLanguage, string> = { de: 'gemäß Angebot', en: 'according to offer' };

/** Optionspositionen der Kalkulation in der Reihenfolge des Angebots (fehlende hinten an). */
export function quoteOptionLines(calc: Pick<QuoteOfferCalc, 'groups'>, offer: QuoteOffer): QuoteCalcLine[] {
  const lines = calc.groups.find((group) => group.key === 'OPTIONS')?.lines ?? [];
  const order = offer.options.items.flatMap((item) => (item.kind === 'line' ? [item.lineId] : []));
  const rank = (id: string) => {
    const index = order.indexOf(id);
    return index === -1 ? order.length : index;
  };
  return [...lines].sort((a, b) => rank(a.id) - rank(b.id));
}

export interface QuoteHandoverOption {
  /** ID der Optionsposition bzw. der eigenen Option des Angebots. */
  id: string;
  title: string;
  longText: string;
  /** Preis wie im Angebot gedruckt (von Hand oder aus der Kalkulation), auf Cent. */
  amountMicros: bigint;
  /** Nur im Angebot angelegt. */
  isCustom: boolean;
}

/**
 * Was der Kunde mitbestellen kann: die Optionen der Kalkulation in der Reihenfolge des
 * Angebots (fehlende hinten an) und die eigenen Optionen des Angebots — jeweils mit dem
 * Preis, der im Angebot steht (gebucht wird, was gedruckt wird).
 */
export function quoteHandoverOptions(calc: QuoteOfferCalc, result: QuoteCalcResult, offer: QuoteOffer, language: OfferLanguage): QuoteHandoverOption[] {
  const pick = (text: { de: string; en: string }) => text[language] || text.de || text.en;
  const results = new Map(result.groups.flatMap((group) => group.lines).map((line) => [line.id, line]));
  const items = new Map(offer.options.items.flatMap((item) => (item.kind === 'line' ? [[item.lineId, item] as const] : [])));
  const fromCalc = quoteOptionLines(calc, offer).map((line): QuoteHandoverOption => {
    const item = items.get(line.id);
    const amount = item?.amountMicros != null ? BigInt(item.amountMicros) : (results.get(line.id)?.totalAmounts.offerMicros ?? 0n);
    return {
      id: line.id,
      title: item?.title ? pick(item.title) : offerLineText(line, language),
      longText: item?.text ? pick(item.text) : offerOptionText(line, language),
      amountMicros: roundToCents(amount),
      isCustom: false,
    };
  });
  const custom = offer.options.items.flatMap((item): QuoteHandoverOption[] =>
    item.kind === 'custom' ? [{ id: item.id, title: pick(item.title) || 'Option', longText: pick(item.text), amountMicros: roundToCents(BigInt(item.amountMicros)), isCustom: true }] : [],
  );
  return [...fromCalc, ...custom];
}

/** Auftragszeilen: Pauschale (Preis des Angebots) + je angenommener Option eine Zeile. */
export function quoteHandoverOrderLines(
  calc: QuoteOfferCalc,
  result: QuoteCalcResult,
  offer: QuoteOffer,
  language: OfferLanguage,
  acceptedOptionLineIds: readonly string[],
): QuoteHandoverOrderLine[] {
  const pick = (text: { de: string; en: string }) => text[language] || text.de || text.en;
  const price = offer.total.priceMicros ?? calc.finalPriceMicros;
  const totalMicros = price !== null ? BigInt(price) : result.withoutOptions.allBasins.offerMicros;
  const subtitles = calc.plants.map((plant) => pick(plant.subtitle) || plant.name).filter(Boolean);
  const lumpSum: QuoteHandoverOrderLine = {
    kind: 'LUMP_SUM',
    lineId: null,
    description: `${calc.name} — ${pick(offer.total.label)}`.slice(0, 500),
    longText: [...subtitles, `${OFFER_REF[language]} ${calc.displayNumber}`].join('\n').slice(0, 5000),
    unitPriceMicros: roundToCents(totalMicros),
  };

  const accepted = new Set(acceptedOptionLineIds);
  const options = quoteHandoverOptions(calc, result, offer, language)
    .filter((option) => accepted.has(option.id))
    .map((option): QuoteHandoverOrderLine => ({
      kind: 'OPTION',
      lineId: option.id,
      description: `${OPTION_PREFIX[language]}: ${option.title}`.slice(0, 500),
      longText: option.longText.slice(0, 5000),
      unitPriceMicros: option.amountMicros,
    }));
  return [lumpSum, ...options];
}

/** Gruppen mit Material (EK nach Hauptwarengruppe); die übrigen werden Sonderkosten. */
const MATERIAL_GROUPS: ReadonlySet<QuoteCalcGroupKey> = new Set(['PLASTIC', 'ELECTRICAL', 'STEEL', 'OPTIONS']);
export const QUOTE_HANDOVER_NO_COMMODITY_LABEL = 'Material ohne Warengruppe';

export interface QuoteHandoverCalcLines {
  /** Je Hauptwarengruppe (1000, 3000 …) die Summe der EK. */
  material: { mainGroupCode: number; plannedAmountMicros: bigint }[];
  /** Montage, Doku, Fracht … und Material ohne Warengruppe. */
  special: { label: string; plannedAmountMicros: bigint }[];
}

/**
 * Vorkalkulation des Projekts aus der Kalkulation: Plan = EK der Positionen (alle
 * Becken), Material nach Hauptwarengruppe des Artikels, nicht angenommene Optionen
 * zählen nicht. Zuschläge (MGK/VGK) übernimmt das Projekt selbst.
 */
export function quoteHandoverCalcLines(
  groups: readonly QuoteCalcGroup[],
  result: QuoteCalcResult,
  acceptedOptionLineIds: readonly string[],
  commodityGroupOf: (articleId: string) => number | null,
): QuoteHandoverCalcLines {
  const accepted = new Set(acceptedOptionLineIds);
  const results = new Map(result.groups.flatMap((group) => group.lines).map((line) => [line.id, line]));
  const material = new Map<number, bigint>();
  const special = new Map<string, bigint>();
  const add = <K>(map: Map<K, bigint>, key: K, amount: bigint) => map.set(key, (map.get(key) ?? 0n) + amount);

  for (const group of groups) {
    for (const line of group.lines) {
      if (group.key === 'OPTIONS' && !accepted.has(line.id)) continue;
      const amount = results.get(line.id)?.totalAmounts.purchaseMicros ?? 0n;
      if (amount === 0n) continue;
      if (!MATERIAL_GROUPS.has(group.key)) {
        add(special, group.title, amount);
        continue;
      }
      const commodity = line.articleId ? commodityGroupOf(line.articleId) : null;
      if (commodity !== null && commodity >= 1000) add(material, Math.floor(commodity / 1000) * 1000, amount);
      else add(special, QUOTE_HANDOVER_NO_COMMODITY_LABEL, amount);
    }
  }
  return {
    material: [...material.entries()].sort((a, b) => a[0] - b[0]).map(([mainGroupCode, plannedAmountMicros]) => ({ mainGroupCode, plannedAmountMicros })),
    special: [...special.entries()].map(([label, plannedAmountMicros]) => ({ label, plannedAmountMicros })),
  };
}
